import { useEffect, useState } from "react";
import { adminApi, apiError } from "@/admin/api";
import { toast } from "sonner";
import { Save, UploadCloud, EyeOff } from "lucide-react";

const Field = ({ k, val, onChange, onJsonState, hint }) => {
  const isComplex = typeof val === "object" && val !== null;
  const [raw, setRaw] = useState(() => (isComplex ? JSON.stringify(val, null, 2) : ""));
  const [invalid, setInvalid] = useState(false);

  if (isComplex) {
    return (
      <div>
        <label className="text-xs font-semibold text-gray-500 uppercase">{k}</label>
        <textarea value={raw} rows={6}
          onChange={(e) => {
            const text = e.target.value;
            setRaw(text);
            try {
              onChange(k, JSON.parse(text));
              setInvalid(false);
              onJsonState(k, false);
            } catch {
              setInvalid(true);
              onJsonState(k, true);
            }
          }}
          className={`w-full mt-1 py-2 px-3 rounded-lg border text-xs font-mono ${invalid ? "border-red-400 bg-red-50" : "border-gray-300"}`} />
        {invalid
          ? <p className="text-xs text-red-600 mt-1">Invalid JSON — your edit is not applied yet. Fix the format before saving.</p>
          : hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
      </div>
    );
  }
  const long = typeof val === "string" && val.length > 60;
  return (
    <div>
      <label className="text-xs font-semibold text-gray-500 uppercase">{k}</label>
      {long
        ? <textarea value={val} rows={3} onChange={(e) => onChange(k, e.target.value)} className="w-full mt-1 py-2 px-3 rounded-lg border border-gray-300 text-sm" />
        : <input value={val} onChange={(e) => onChange(k, e.target.value)} className="w-full mt-1 py-2 px-3 rounded-lg border border-gray-300 text-sm" />}
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  );
};

const USAGE = {
  home: "Shown live on: homepage hero (heading, description, button, background image) and impact stats. Note: the hero text appears in the English site view; the Hindi view uses built-in translations.",
  mission: "Shown live on: homepage story section (title + text).",
  about: "Shown live on: About page intro (title + lead text) at /about.",
  testimonials: "Shown live on: homepage testimonials section.",
  contact: "Not displayed directly — contact details are edited in Settings.",
  vision: "Not currently displayed on the site.",
  gallery: "Not currently displayed on the site.",
  faq: "Not currently displayed on the site.",
  footer: "Not currently displayed on the site.",
  privacy: "Not used — the Privacy Policy page text is fixed for bank compliance.",
  terms: "Not used — the Terms page text is fixed for bank compliance.",
  donation_page: "Not currently displayed on the site.",
};

const FIELD_HINTS = {
  hero_image: "Paste an image URL from the Media Library (Upload → URL button). Leave empty for the default image.",
};

export default function Content() {
  const [list, setList] = useState([]);
  const [key, setKey] = useState(null);
  const [doc, setDoc] = useState(null);
  const [draft, setDraft] = useState({});
  const [invalidJson, setInvalidJson] = useState({});

  const loadList = () => adminApi.get("/admin/content").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { loadList(); }, []);

  const openKey = async (k) => {
    setKey(k);
    setInvalidJson({});
    const { data } = await adminApi.get(`/admin/content/${k}`);
    setDoc(data);
    setDraft(data.draft || data.published || {});
  };

  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const setJsonState = (k, bad) => setInvalidJson((s) => ({ ...s, [k]: bad }));
  const hasInvalid = Object.values(invalidJson).some(Boolean);

  const saveDraft = async () => {
    try { await adminApi.put(`/admin/content/${key}`, { content: draft }); toast.success("Draft saved — not live yet. Click Save & Publish to update the website."); loadList(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const publish = async () => {
    try { await adminApi.put(`/admin/content/${key}`, { content: draft }); await adminApi.post(`/admin/content/${key}/publish`); toast.success("Published live"); loadList(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const unpublish = async () => {
    try { await adminApi.post(`/admin/content/${key}/unpublish`); toast.success("Unpublished"); loadList(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="grid lg:grid-cols-4 gap-6">
      <div className="lg:col-span-1">
        <h1 className="text-xl font-bold text-gray-900 mb-4">Content (CMS)</h1>
        <div className="bg-white rounded-xl border border-gray-200 divide-y" data-testid="content-list">
          {list.map((c) => (
            <button key={c.key} onClick={() => openKey(c.key)} data-testid={`content-${c.key}`}
                    className={`w-full text-left px-4 py-3 text-sm flex items-center justify-between ${key === c.key ? "bg-gray-50" : ""}`}>
              <span className="capitalize text-gray-800">{c.key.replace(/_/g, " ")}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${c.status === "published" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{c.status}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="lg:col-span-3">
        {!doc ? <div className="text-gray-400 mt-10">Select a page to edit.</div> : (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
              <h2 className="font-semibold text-gray-900 capitalize">{key.replace(/_/g, " ")}</h2>
              <div className="flex gap-2">
                <button data-testid="save-publish" onClick={publish} disabled={hasInvalid} className="inline-flex items-center gap-1.5 rounded-lg bg-[hsl(13_65%_54%)] text-white px-3 py-2 text-sm disabled:opacity-50"><UploadCloud size={14} /> Save &amp; Publish</button>
                <button data-testid="save-draft" onClick={saveDraft} disabled={hasInvalid} className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:opacity-50"><Save size={14} /> Save Draft</button>
                <button data-testid="unpublish" onClick={unpublish} className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm"><EyeOff size={14} /> Unpublish</button>
              </div>
            </div>
            <p data-testid="content-usage" className="text-xs text-gray-500 mb-5">{USAGE[key] || ""}</p>
            <div className="space-y-4">
              {Object.entries(draft).map(([k, v]) => <Field key={`${key}-${k}`} k={k} val={v} onChange={setField} onJsonState={setJsonState} hint={FIELD_HINTS[k]} />)}
            </div>
            <p className="text-xs text-gray-400 mt-4">Complex fields (lists/objects) are edited as JSON — invalid JSON is blocked from saving. Save &amp; Publish makes changes live on the public website instantly; Save Draft keeps them private.</p>
          </div>
        )}
      </div>
    </div>
  );
}
